<script setup>
/* global axios */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DEFAULT_CURRENCY } from '../utils/currencyUtils';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import kanbanAPI from '../../../../api/kanban';

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  'update:item',
  'item-updated'
]);

const { t, locale } = useI18n();
const movingStageActivityId = ref(null);

// Computed properties para atividades
const activities = computed(() => {
  return props.item?.activities || [];
});

// Helper functions para buscar dados
const findNoteByActivity = (activity) => {
  const notes = props.item?.item_details?.notes;
  if (!Array.isArray(notes)) return null;
  return notes.find(note =>
    new Date(note.created_at).getTime() === new Date(activity.created_at).getTime()
  );
};

const findChecklistItemByActivity = (activity) => {
  const checklist = props.item?.item_details?.checklist;
  if (!Array.isArray(checklist)) return null;
  return checklist.find(item =>
    new Date(item.created_at).getTime() === new Date(activity.created_at).getTime()
  );
};

const findAttachmentByActivity = (activity) => {
  const attachments = props.item?.item_details?.attachments;
  if (!Array.isArray(attachments)) return null;
  return attachments.find(att =>
    new Date(att.created_at).getTime() === new Date(activity.created_at).getTime()
  );
};

const getStageName = (stageId) => {
  if (!stageId) return '-';

  const stages =
    props.item?.funnel?.stages ?? props.item?.item_details?.funnel?.stages;
  if (!stages || typeof stages !== 'object') return stageId;

  const stage =
    stages[stageId] ?? stages[String(stageId)];
  const stageName = stage?.name;
  return stageName || stageId;
};

const resolveActivityStage = activity => {
  if (activity?.type !== 'stage_changed') return null;
  const rawDetails = activity?.rawDetails || activity?.details;
  if (!rawDetails || typeof rawDetails !== 'object') return null;
  return rawDetails.new_stage || null;
};

const canMoveToActivityStage = activity => {
  const targetStage = resolveActivityStage(activity);
  if (!targetStage || !props.item?.id) return false;
  return targetStage !== props.item?.funnel_stage;
};

const moveItemToActivityStage = async activity => {
  const targetStage = resolveActivityStage(activity);
  if (!targetStage || !props.item?.id || !canMoveToActivityStage(activity)) {
    return;
  }

  try {
    movingStageActivityId.value = activity.id;
    const updatedItem = await kanbanAPI.moveToStage(
      props.item.id,
      targetStage,
      props.item.funnel_id
    );
    if (updatedItem) {
      emit('update:item', updatedItem);
      emit('item-updated');
    }
  } catch (error) {
    // Mantém comportamento silencioso como no restante da aba de atividades.
  } finally {
    movingStageActivityId.value = null;
  }
};

const history = computed(() => {
  return activities.value
    .map(activity => {
      let icon, title, details;

      switch (activity.type) {
        case 'attachment_added':
          icon = 'attach';
          title = t('KANBAN.HISTORY.ATTACHMENT_ADDED');
          const attachment = findAttachmentByActivity(activity);
          details = attachment
            ? `${attachment.filename} (${(attachment.byte_size / 1024).toFixed(1)} KB)`
            : `${t('KANBAN.HISTORY.FILE')}: ${activity.details.filename || 'Arquivo'}`;
          break;

        case 'note_added':
          icon = 'comment-add';
          title = t('KANBAN.HISTORY.NOTE_ADDED');
          const note = findNoteByActivity(activity);
          details = note ? note.text : activity.details.note_text || 'Nota adicionada';
          break;

        case 'checklist_item_added':
          icon = 'add-circle';
          title = t('KANBAN.HISTORY.CHECKLIST_ITEM_ADDED');
          const checklistItem = findChecklistItemByActivity(activity);
          details = checklistItem ? checklistItem.text : activity.details.item_text || 'Item adicionado';
          break;

        case 'checklist_item_toggled':
          icon = activity.details.completed ? 'checkmark' : 'dismiss';
          title = t('KANBAN.HISTORY.CHECKLIST_ITEM_UPDATED');
          const toggledItem = findChecklistItemByActivity(activity);
          const itemText = toggledItem ? toggledItem.text : activity.details.item_text || 'Item';
          details = `${itemText} - ${
            activity.details.completed
              ? t('KANBAN.HISTORY.COMPLETED')
              : t('KANBAN.HISTORY.PENDING')
          }`;
          break;

        case 'priority_changed':
          icon = 'alert';
          title = t('KANBAN.HISTORY.PRIORITY_CHANGED');
          details = `${
            activity.details.old_priority || t('KANBAN.HISTORY.NONE')
          } → ${activity.details.new_priority}`;
          break;

        case 'status_changed':
          icon = 'status';
          title = t('KANBAN.HISTORY.STATUS_CHANGED');
          details = `${
            activity.details.old_status || t('KANBAN.HISTORY.PENDING')
          } → ${activity.details.new_status}`;
          break;

        case 'stage_changed':
          icon = 'arrow-right';
          title = t('KANBAN.HISTORY.STAGE_CHANGED');
          const stageUser = activity.details.user || activity.user;
          details = `${getStageName(activity.details.old_stage)} → ${
            getStageName(activity.details.new_stage)
          }${stageUser ? ` (${stageUser.name})` : ''}`;
          break;

        case 'value_changed':
          icon = 'credit-card-person';
          title = t('KANBAN.HISTORY.VALUE_CHANGED');
          const oldValue = activity.details.old_value || '0';
          const newValue = activity.details.new_value;
          const currency = activity.details.currency?.symbol || DEFAULT_CURRENCY.symbol;
          details = `${currency} ${oldValue} → ${currency} ${newValue}`;
          break;

        case 'agent_changed':
          icon = 'person';
          title = t('KANBAN.HISTORY.AGENT_CHANGED');
          details = `${
            activity.details.old_agent || t('KANBAN.HISTORY.NONE')
          } → ${activity.details.new_agent || t('KANBAN.HISTORY.NONE')}`;
          break;

        case 'agent_assigned':
          icon = 'person-add';
          title = t('KANBAN.HISTORY.AGENT_ASSIGNED');
          details = `${activity.details.agent_name || t('KANBAN.HISTORY.UNKNOWN_AGENT')} ${t('KANBAN.HISTORY.WAS_ASSIGNED')}`;
          break;

        case 'agent_removed':
          icon = 'dismiss-circle';
          title = t('KANBAN.HISTORY.AGENT_REMOVED');
          details = `${activity.details.agent_name || t('KANBAN.HISTORY.UNKNOWN_AGENT')} ${t('KANBAN.HISTORY.WAS_REMOVED')}`;
          break;

        case 'title_changed':
          icon = 'edit';
          title = t('KANBAN.HISTORY.TITLE_CHANGED');
          details = `"${activity.details.old_title || ''}" → "${activity.details.new_title || ''}"`;
          break;

        case 'description_changed':
          icon = 'text-description';
          title = t('KANBAN.HISTORY.DESCRIPTION_CHANGED');
          details = t('KANBAN.HISTORY.DESCRIPTION_UPDATED');
          break;

        case 'deadline_changed':
          icon = 'calendar-clock';
          title = t('KANBAN.HISTORY.DEADLINE_CHANGED');
          const oldDeadline = activity.details.old_deadline ? new Date(activity.details.old_deadline).toLocaleDateString(locale.value || 'en') : t('KANBAN.HISTORY.NONE');
          const newDeadline = activity.details.new_deadline ? new Date(activity.details.new_deadline).toLocaleDateString(locale.value || 'en') : t('KANBAN.HISTORY.NONE');
          details = `${oldDeadline} → ${newDeadline}`;
          break;

        case 'timer_started':
          icon = 'play';
          title = t('KANBAN.HISTORY.TIMER_STARTED');
          details = t('KANBAN.HISTORY.TIMER_STARTED_DESC');
          break;

        case 'timer_stopped':
          icon = 'pause';
          title = t('KANBAN.HISTORY.TIMER_STOPPED');
          const duration = activity.details.duration ? `${Math.floor(activity.details.duration / 60)}min ${activity.details.duration % 60}s` : '';
          details = duration ? `${t('KANBAN.HISTORY.TIMER_STOPPED_DESC')} (${duration})` : t('KANBAN.HISTORY.TIMER_STOPPED_DESC');
          break;

        case 'note_updated':
          icon = 'edit';
          title = t('KANBAN.HISTORY.NOTE_UPDATED');
          details = `${activity.details.old_text || ''} → ${activity.details.new_text || ''}`;
          break;

        case 'note_removed':
          icon = 'delete';
          title = t('KANBAN.HISTORY.NOTE_REMOVED');
          details = activity.details.note_text || t('KANBAN.HISTORY.NOTE_DELETED');
          break;

        case 'checklist_item_removed':
          icon = 'delete';
          title = t('KANBAN.HISTORY.CHECKLIST_ITEM_REMOVED');
          details = activity.details.item_text || t('KANBAN.HISTORY.ITEM_REMOVED');
          break;

        case 'checklist_item_updated':
          icon = 'edit';
          title = t('KANBAN.HISTORY.CHECKLIST_ITEM_UPDATED');
          details = `"${activity.details.old_text || ''}" → "${activity.details.new_text || ''}"`;
          break;

        case 'attachment_removed':
          icon = 'delete';
          title = t('KANBAN.HISTORY.ATTACHMENT_REMOVED');
          details = activity.details.filename || t('KANBAN.HISTORY.FILE_DELETED');
          break;

        case 'item_created':
          icon = 'add';
          title = t('KANBAN.HISTORY.ITEM_CREATED');
          details = `"${activity.details.title || t('KANBAN.HISTORY.NEW_ITEM')}" ${t('KANBAN.HISTORY.IN_STAGE')} ${
            activity.details.funnel_stage
              ? getStageName(activity.details.funnel_stage)
              : t('KANBAN.HISTORY.UNKNOWN_STAGE')
          }`;
          break;

        case 'custom_attribute_changed':
          icon = 'settings';
          title = t('KANBAN.HISTORY.CUSTOM_ATTRIBUTE_CHANGED');
          details = `${activity.details.attribute_name}: "${activity.details.old_value || ''}" → "${activity.details.new_value || ''}"`;
          break;

        case 'automated_action_executed':
          icon = 'bot';
          title = t('KANBAN.HISTORY.AUTOMATED_ACTION');
          const actionType = activity.details.action_type || 'unknown';
          let actionDesc = '';
          switch (actionType) {
            case 'move_to_stage':
              actionDesc = `${t('KANBAN.HISTORY.MOVED_TO_STAGE')} ${getStageName(activity.details.details?.new_stage)}`;
              break;
            case 'assign_agent':
              actionDesc = `${t('KANBAN.HISTORY.ASSIGNED_AGENT')} ${activity.details.details?.agent_id || ''}`;
              break;
            case 'create_note':
              actionDesc = t('KANBAN.HISTORY.CREATED_AUTOMATED_NOTE');
              break;
            case 'update_checklist':
              actionDesc = `${t('KANBAN.HISTORY.UPDATED_CHECKLIST')} (${activity.details.details?.checklist_updates || 0} ${t('KANBAN.HISTORY.ITEMS')})`;
              break;
            case 'notify_team':
              actionDesc = t('KANBAN.HISTORY.NOTIFIED_TEAM');
              break;
            default:
              actionDesc = actionType;
          }
          details = actionDesc;
          break;

        case 'conversation_linked': {
          icon = 'chat';
          title = t('KANBAN.HISTORY.CONVERSATION_LINKED');
          const convUser = activity.details.user || activity.user;
          const d = activity.details || {};
          const convId =
            d.conversation_id ??
            d.display_id ??
            props.item?.conversation?.display_id ??
            props.item?.conversation_display_id ??
            props.item?.item_details?.conversation_id;
          const convTitle =
            d.conversation_title ??
            props.item?.conversation?.last_message?.content ??
            t('KANBAN.ITEM_CONVERSATION.TITLE');
          details = `${convUser?.name || t('KANBAN.SYSTEM')} ${t(
            'KANBAN.HISTORY.LINKED_CONVERSATION'
          )} #${convId ?? '—'} - ${convTitle}`;
          break;
        }

        default:
          icon = 'info';
          title = `${t('KANBAN.HISTORY.ACTIVITY')}: ${activity.type}`;
          details = JSON.stringify(activity.details);
      }

      return {
        ...activity,
        rawDetails: activity.details,
        icon,
        title,
        details,
      };
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Ordena por data decrescente
});

// Função para formatar data de forma compacta
const formatDate = dateString => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    // Se for hoje, mostra apenas hora
    const appLocale = locale.value || 'en';
    if (diffHours < 24 && date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString(appLocale, {
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return new Intl.RelativeTimeFormat(appLocale, { numeric: 'auto' }).format(-1, 'day');
    }

    if (diffDays < 7) {
      return date.toLocaleDateString(appLocale, { weekday: 'short' });
    }

    return date.toLocaleDateString(appLocale, {
      day: '2-digit',
      month: '2-digit',
    });
  } catch (error) {
    return dateString;
  }
};
</script>

<template>
  <!-- Container principal com estilo consistente -->
  <div class="activities-container bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
    <!-- Cabeçalho da aba com indicador de pulso -->
    <div class="activities-header flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <div class="w-2 h-2 bg-woot-500 rounded-full animate-pulse"></div>
        <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
          {{ t('KANBAN.TABS.ACTIVITIES') }}
        </h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-3 py-1 rounded-full font-medium">
          {{ activities.length }} {{ t('KANBAN.HISTORY.ACTIVITIES') }}
        </span>
      </div>
    </div>

    <!-- Mensagem quando não há atividades -->
    <div
      v-if="activities.length === 0"
      class="text-center py-8 text-slate-500 dark:text-slate-400"
    >
      <fluent-icon icon="calendar-clock" size="32" class="mx-auto mb-4 opacity-50" />
      <p class="text-sm">{{ t('KANBAN.HISTORY.NO_ACTIVITY') }}</p>
    </div>

    <!-- Timeline de atividades -->
    <div v-else class="activities-timeline">
      <div
        v-for="(event, index) in history"
        :key="event.id"
        class="activity-item"
      >
        <!-- Ícone e linha de conexão -->
        <div class="activity-connector">
          <div class="activity-icon">
            <fluent-icon :icon="event.icon" size="11" />
          </div>
          <!-- Linha de conexão (não mostrar na última atividade) -->
          <div
            v-if="index < history.length - 1"
            class="activity-line"
          ></div>
        </div>

        <!-- Conteúdo do evento -->
        <div class="activity-content">
          <div class="activity-header">
            <div class="flex items-center gap-2">
              <span class="activity-title">
                {{ event.title }}
              </span>
              <button
                v-if="canMoveToActivityStage(event)"
                type="button"
                class="inline-flex items-center gap-1 rounded-md border border-woot-200 bg-woot-50 px-2.5 py-1 text-xs font-medium text-woot-600 underline transition-colors hover:bg-woot-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-woot-700 dark:bg-woot-900/20 dark:text-woot-300 dark:hover:bg-woot-900/30"
                :disabled="movingStageActivityId === event.id"
                @click="moveItemToActivityStage(event)"
              >
                <svg
                  v-if="movingStageActivityId === event.id"
                  class="h-3 w-3 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    stroke="currentColor"
                    stroke-opacity="0.25"
                    stroke-width="2"
                  />
                  <path
                    d="M21 12a9 9 0 0 0-9-9"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
                <svg
                  v-else
                  class="h-3 w-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 5L3 11L9 17"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 11H13C17.4183 11 21 14.5817 21 19"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{
                  movingStageActivityId === event.id
                    ? t('KANBAN.HISTORY.MOVING_TO_STAGE')
                    : t('KANBAN.HISTORY.BACK_TO_STAGE')
                }}
              </button>
            </div>
            <span class="activity-date">
              {{ formatDate(event.created_at) }}
            </span>
          </div>

          <div class="activity-details">
            <p class="activity-text">{{ event.details }}</p>
          </div>

          <div v-if="event.user && event.user.name" class="activity-author">
            <Avatar
              :name="event.user.name"
              :src="event.user.avatar_url"
              :size="20"
            />
            <span class="text-xs text-slate-600 dark:text-slate-400">
              {{ event.user.name }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Animações do componente de atividades */
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes activity-pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
}

/* Container principal */
.activities-container {
  animation: fade-in-up 0.6s ease-out;
}

.activities-header {
  animation: fade-in-up 0.4s ease-out;
}

.activities-timeline {
  @apply relative;
}

.activity-item {
  @apply flex gap-4 mb-3;
}

.activity-connector {
  @apply flex flex-col items-center;
}

.activity-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: rgb(241 245 249) !important;
  color: rgb(17 24 39) !important;
  width: 40px !important;
  height: 40px !important;
  padding: 6px !important;
  border-radius: 20px !important;
  box-sizing: border-box !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.activity-icon:hover {
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.activity-line {
  @apply w-px h-full bg-slate-200 dark:bg-slate-600 mt-2;
  background: linear-gradient(180deg,
    rgba(148, 163, 184, 0.3),
    rgba(148, 163, 184, 0.6),
    rgba(148, 163, 184, 0.3)
  );
}

/* Dark mode adjustments */
.dark .activity-line {
  background: linear-gradient(180deg,
    rgba(71, 85, 105, 0.3),
    rgba(71, 85, 105, 0.6),
    rgba(71, 85, 105, 0.3)
  );
}

.activity-content {
  @apply flex-1 min-w-0;
}

.activity-header {
  @apply flex items-center justify-between mb-0.5;
}

.activity-title {
  @apply font-medium text-sm text-slate-900 dark:text-slate-100 whitespace-nowrap;
}

.activity-date {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.activity-details {
  @apply mb-1;
}

.activity-text {
  @apply text-sm text-slate-700 dark:text-slate-300 leading-relaxed;
}

.activity-author {
  @apply flex items-center gap-2;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .activities-container {
    padding: 1rem;
  }

  .activity-icon {
    width: 2rem !important;
    height: 2rem !important;
  }

  .activity-icon fluent-icon {
    font-size: 0.65rem;
  }

  .activity-item {
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }
}
</style>
